import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { highlightBolehlang } from '../../lib/bolehlang-prism';
import {
  Page,
  TopNav,
  LogoLockup,
  LogoImg,
  Hero,
  Eyebrow,
  HeroTitle,
  Hash,
  TitleAccent,
  HeroLead,
  InstallWrap,
  InstallBox,
  InstallPrefix,
  InstallCode,
  CopyBtn,
  DownloadRow,
  DlButtonWrap,
  DlButton,
  DlIcon,
  DlOsLabel,
  DlOsName,
  DlOsVersion,
  DlChevron,
  DlDropdown,
  DlDropItem,
  DlDropArch,
  BuildLabel,
  HeroActions,
  BtnPrimary,
  BtnGhost,
  ExamplesSection,
  ExamplesShell,
  ExamplesTop,
  ExampleTabList,
  ExampleTabBtn,
  ExamplesMeta,
  FileTab,
  SnippetCopyBtn,
  ExamplesPanes,
  EditorPane,
  PaneLabel,
  ExampleReadonly,
  OutputPane,
  OutputBody,
  Section,
  SectionTitle,
  FeatureGrid,
  FeatureCard,
  FeatureIcon,
  FeatureTitle,
  FeatureText,
  FeatureSnippetStack,
  FeatureSnippetBlock,
  ExceptionStack,
  ExceptionLine,
  ExName,
  SyntaxSection,
  SyntaxLead,
  KeywordTableWrap,
  KeywordTable,
  KwThead,
  KwTh,
  KwTdBoleh,
  KwTdConv,
  KwTdMean,
  CtaBand,
  CtaInner,
  CtaKicker,
  CtaTitle,
  CtaBtn,
  CtaActions,
  CtaBtnAlt,
  Footer,
  FooterLogo,
  FooterNote,
  FooterCopy,
} from './styles';

const GITHUB = 'https://github.com/kooixh/bolehlang';
const DOCS = 'https://github.com/kooixh/bolehlang/tree/master/docs';

const VERSION = 'v0.0.1';
const DL_BASE = `https://github.com/kooixh/bolehlang/releases/download/${VERSION}`;
const DOWNLOADS = [
  {
    os: 'macOS',
    icon: '🍎',
    variants: [
      { arch: 'ARM64 (Apple Silicon)', url: `${DL_BASE}/bolehlang_0.0.1_macos_arm64.tar.gz` },
      { arch: 'x86_64 (Intel)', url: `${DL_BASE}/bolehlang_0.0.1_macos_x86_64.tar.gz` },
    ],
  },
  {
    os: 'Linux',
    icon: '🐧',
    variants: [
      { arch: 'ARM64', url: `${DL_BASE}/bolehlang_0.0.1_linux_arm64.tar.gz` },
      { arch: 'x86_64', url: `${DL_BASE}/bolehlang_0.0.1_linux_x86_64.tar.gz` },
    ],
  },
  {
    os: 'Windows',
    icon: '🪟',
    variants: [
      { arch: 'ARM64', url: `${DL_BASE}/bolehlang_0.0.1_windows_arm64.zip` },
      { arch: 'x86_64', url: `${DL_BASE}/bolehlang_0.0.1_windows_x86_64.zip` },
    ],
  },
];

const INSTALL_CMD =
  'git clone https://github.com/kooixh/bolehlang.git';

/** Short sample; lines kept narrow for the feature card. */
const NASI_CAMPUR_SNIPPET = `// tapau "lauk.boleh"

jom scoop(x) {
    nah "→ " + x;
}
dei n = 0;
one by one (n < 2) {
    is it (n sama 0) {
        eh scoop("nasi");
    } abuden {
        eh scoop("kuah");
    }
    tukar n += 1;
}
try see {
    mampus "Habis!";
} alamak (e) {
    eh e;
} confirm need {
    eh "Ok lah";
}`;

/** Full keyword ↔ conventional mapping (matches repo README keyword reference). */
const KEYWORD_ROWS = [
  { boleh: 'dei', conventional: 'let / var', meaning: 'Declare a variable' },
  { boleh: 'memang dei', conventional: 'const', meaning: 'Declare a constant' },
  { boleh: 'tukar', conventional: 'reassign', meaning: 'Reassign a variable' },
  { boleh: 'eh', conventional: 'print', meaning: 'Print to console' },
  { boleh: 'is it', conventional: 'if', meaning: 'Conditional check' },
  { boleh: 'or is it', conventional: 'else if', meaning: 'Alternate condition' },
  { boleh: 'abuden', conventional: 'else', meaning: 'Default branch ("obviously lah")' },
  { boleh: 'one by one', conventional: 'for / while', meaning: 'Loop' },
  { boleh: 'sampai', conventional: 'to / until', meaning: 'Range end' },
  { boleh: 'dalam', conventional: 'in', meaning: 'Iterate over collection' },
  { boleh: 'gaodim', conventional: 'break', meaning: 'Break from loop ("enough already")' },
  { boleh: 'no need', conventional: 'continue', meaning: 'Skip iteration' },
  { boleh: 'jom', conventional: 'function', meaning: 'Declare function ("let\'s go")' },
  { boleh: 'nah', conventional: 'return', meaning: 'Return value ("here, take it")' },
  { boleh: 'can', conventional: 'true', meaning: 'Boolean true' },
  { boleh: 'cannot', conventional: 'false', meaning: 'Boolean false' },
  { boleh: 'kosong', conventional: 'null', meaning: 'Null value ("empty")' },
  { boleh: 'sama', conventional: '==', meaning: 'Equal' },
  { boleh: 'tak sama', conventional: '!=', meaning: 'Not equal' },
  { boleh: 'also', conventional: '&&', meaning: 'Logical AND' },
  { boleh: 'if not', conventional: '||', meaning: 'Logical OR' },
  { boleh: 'try see', conventional: 'try', meaning: 'Try block' },
  { boleh: 'alamak', conventional: 'catch', meaning: 'Catch block ("oh no!")' },
  { boleh: 'confirm need', conventional: 'finally', meaning: 'Finally block' },
  { boleh: 'mampus', conventional: 'throw', meaning: 'Throw error ("you\'re dead")' },
  { boleh: 'tapau', conventional: 'import', meaning: 'Import a file ("takeaway")' },
  { boleh: 'cincai', conventional: 'random', meaning: 'Random number ("whatever/random")' },
];

const BUILTIN_ROWS = [
  { fn: 'eh', desc: 'Print to stdout', example: 'eh "Hello!"' },
  { fn: 'walao eh', desc: 'Print warning to stderr', example: 'walao eh "Careful!"' },
  { fn: 'len(x)', desc: 'Get length of string or array', example: 'len("hello") → 5' },
  { fn: 'apa_ni(x)', desc: 'Get the type of a value', example: 'apa_ni(42) → "nombor"' },
  { fn: 'masuk(arr, val)', desc: 'Append value(s) to array', example: 'masuk(arr, "new")' },
  { fn: 'potong_stim(arr, s, e)', desc: 'Slice an array', example: 'potong_stim(arr, 1, 3)' },
  { fn: 'nombor(x)', desc: 'Convert to number', example: 'nombor("42") → 42' },
  { fn: 'string(x)', desc: 'Convert to string', example: 'string(42) → "42"' },
  { fn: 'cincai()', desc: 'Random float 0–1', example: 'cincai() → 0.734…' },
  { fn: 'cincai(max)', desc: 'Random int 0 to max (exclusive)', example: 'cincai(10) → 0–9' },
  { fn: 'cincai(min, max)', desc: 'Random int min to max (inclusive)', example: 'cincai(1, 6) → 1–6' },
];

const EXAMPLES = [
  {
    id: 'snack',
    label: 'Snack order',
    file: 'snack.boleh',
    code: `dei price = 4.50;
dei hungry = can;

is it (hungry) {
    eh "Nasi Lemak! RM" + price;
} abuden {
    eh "Wait first lah";
}`,
    output: 'Nasi Lemak! RM4.5',
  },
  {
    id: 'hello',
    label: 'Hello world',
    file: 'hello.boleh',
    code: `eh "Hello World! Apa khabar?";`,
    output: 'Hello World! Apa khabar?',
  },
  {
    id: 'variables',
    label: 'Variables',
    file: 'variables.boleh',
    code: `// eh: declare variables
dei nama = "Ali";
dei umur = 25;
dei programmer = can;

eh "Nama: " + nama;
eh "Umur: " + umur;
eh "Programmer: " + programmer;

memang dei PI = 3.14159;
eh "PI = " + PI;

tukar umur = 26;
eh "Happy Birthday! Now umur: " + umur;`,
    output: [
      'Nama: Ali',
      'Umur: 25',
      'Programmer: can',
      'PI = 3.14159',
      'Happy Birthday! Now umur: 26',
    ].join('\n'),
  },
  {
    id: 'loops',
    label: 'Loops',
    file: 'loops.boleh',
    code: `// eh: while-style loop
dei count = 0;
one by one (count < 5) {
    eh "Count: " + count;
    tukar count += 1;
}

eh "---";
one by one (i = 1 sampai 5) {
    eh "Number: " + i;
}

eh "---";
dei fruits = ["durian", "rambutan", "mangosteen", "cempedak"];
one by one (buah dalam fruits) {
    eh "Buah: " + buah;
}`,
    output: [
      'Count: 0',
      'Count: 1',
      'Count: 2',
      'Count: 3',
      'Count: 4',
      '---',
      'Number: 1',
      'Number: 2',
      'Number: 3',
      'Number: 4',
      'Number: 5',
      '---',
      'Buah: durian',
      'Buah: rambutan',
      'Buah: mangosteen',
      'Buah: cempedak',
    ].join('\n'),
  },
  {
    id: 'fizzbuzz',
    label: 'FizzBuzz',
    file: 'fizzbuzz.boleh',
    code: `one by one (i = 1 sampai 15) {
    is it (i % 15 sama 0) {
        eh "FizzBuzz";
    } or is it (i % 3 sama 0) {
        eh "Fizz";
    } or is it (i % 5 sama 0) {
        eh "Buzz";
    } abuden {
        eh i;
    }
}`,
    output: [
      '1',
      '2',
      'Fizz',
      '4',
      'Buzz',
      'Fizz',
      '7',
      '8',
      'Fizz',
      'Buzz',
      '11',
      'Fizz',
      '13',
      '14',
      'FizzBuzz',
    ].join('\n'),
  },
  {
    id: 'trycatch',
    label: 'Try / catch',
    file: 'trycatch.boleh',
    code: `try see {
    eh "Cuba buat sesuatu...";
    mampus "Alamak something went wrong!";
    eh "This won't print";
} alamak (err) {
    eh "Kantoi! Error: " + err;
} confirm need {
    eh "Mesti buat: cleanup done";
}`,
    output: [
      'Cuba buat sesuatu...',
      'Kantoi! Error: Alamak something went wrong!',
      'Mesti buat: cleanup done',
    ].join('\n'),
  },
];

export default function Welcome() {
  const [installCopied, setInstallCopied] = useState(false);
  const [activeId, setActiveId] = useState(EXAMPLES[0].id);
  const [snippetCopied, setSnippetCopied] = useState(false);
  const [openDl, setOpenDl] = useState(null);
  const dlRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dlRef.current && !dlRef.current.contains(e.target)) setOpenDl(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const active = useMemo(
    () => EXAMPLES.find((e) => e.id === activeId) ?? EXAMPLES[0],
    [activeId],
  );

  const highlighted = useMemo(() => highlightBolehlang(active.code), [active.code]);

  const copyInstall = useCallback(() => {
    navigator.clipboard.writeText(INSTALL_CMD).then(() => {
      setInstallCopied(true);
      setTimeout(() => setInstallCopied(false), 2000);
    });
  }, []);

  const copySnippet = useCallback(() => {
    navigator.clipboard.writeText(active.code).then(() => {
      setSnippetCopied(true);
      setTimeout(() => setSnippetCopied(false), 2000);
    });
  }, [active.code]);

  return (
    <Page>
      <TopNav>
        <LogoLockup>
          <LogoImg src="/bolehlang_logo.svg" alt="bolehlang logo" />
        </LogoLockup>
      </TopNav>

      <Hero id="top">
        <Eyebrow>The Malaysian programming language</Eyebrow>
        <HeroTitle>
          <Hash>#</Hash>
          <TitleAccent>BOLEH!</TitleAccent> Lang
        </HeroTitle>
        <HeroLead>
          <strong>No need paiseh one.</strong>
          <br />
          Just code like you talk, <strong>Malaysia boleh!</strong> 
        </HeroLead>

        <DownloadRow ref={dlRef}>
          {DOWNLOADS.map((dl) => (
            <DlButtonWrap key={dl.os}>
              <DlButton
                type="button"
                onClick={() => setOpenDl(openDl === dl.os ? null : dl.os)}
              >
                <DlIcon>{dl.icon}</DlIcon>
                <DlOsLabel>
                  <DlOsName>{dl.os}</DlOsName>
                  <DlOsVersion>{VERSION}</DlOsVersion>
                </DlOsLabel>
                <DlChevron $open={openDl === dl.os}>▼</DlChevron>
              </DlButton>
              {openDl === dl.os && (
                <DlDropdown>
                  {dl.variants.map((v) => (
                    <DlDropItem
                      key={v.arch}
                      href={v.url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => setOpenDl(null)}
                    >
                      ⬇ <DlDropArch>{v.arch}</DlDropArch>
                    </DlDropItem>
                  ))}
                </DlDropdown>
              )}
            </DlButtonWrap>
          ))}
        </DownloadRow>

        <BuildLabel>If you want to gaodim yourself...</BuildLabel>

        <InstallWrap>
          <InstallBox>
            <InstallPrefix>$</InstallPrefix>
            <InstallCode>{INSTALL_CMD}</InstallCode>
            <CopyBtn type="button" onClick={copyInstall}>
              {installCopied ? 'Copied!' : 'Copy'}
            </CopyBtn>
          </InstallBox>
        </InstallWrap>

        <HeroActions>
          <BtnPrimary href="#examples">Examples</BtnPrimary>
          <BtnGhost href={DOCS}>
            Documentation
          </BtnGhost>
          <BtnGhost href={GITHUB} target="_blank" rel="noreferrer">
            GitHub
          </BtnGhost>
        </HeroActions>
      </Hero>

      <ExamplesSection id="examples">
        <ExamplesShell>
          <ExamplesTop>
            <ExampleTabList>
              {EXAMPLES.map((ex) => (
                <ExampleTabBtn
                  key={ex.id}
                  type="button"
                  $active={activeId === ex.id}
                  onClick={() => setActiveId(ex.id)}
                >
                  {ex.label}
                </ExampleTabBtn>
              ))}
            </ExampleTabList>
            <ExamplesMeta>
              <FileTab>{active.file}</FileTab>
              <SnippetCopyBtn type="button" onClick={copySnippet}>
                {snippetCopied ? 'Copied!' : 'Copy snippet'}
              </SnippetCopyBtn>
            </ExamplesMeta>
          </ExamplesTop>
          <ExamplesPanes>
            <EditorPane>
              <PaneLabel>SOURCE</PaneLabel>
              <ExampleReadonly>
                <pre>
                  <code
                    className="language-bolehlang"
                    dangerouslySetInnerHTML={{ __html: highlighted }}
                  />
                </pre>
              </ExampleReadonly>
            </EditorPane>
            <OutputPane>
              <PaneLabel>OUTPUT</PaneLabel>
              <OutputBody>{active.output}</OutputBody>
            </OutputPane>
          </ExamplesPanes>
        </ExamplesShell>
      </ExamplesSection>

      <Section id="features">
        <SectionTitle>Bolehlang memang best!</SectionTitle>
        <FeatureGrid>
          <FeatureCard>
            <FeatureIcon aria-hidden>⚠️</FeatureIcon>
            <FeatureTitle>Familiar Exception</FeatureTitle>
            <FeatureText>
              Error messages are written in Manglish so you instantly get what went wrong — no
              decoding cryptic stack traces.
              <br />
              Straight from the interpreter:
            </FeatureText>
            <ExceptionStack>
              <ExceptionLine>
                <ExName>MakKauHijauException</ExName>:<br />
                cannot index kosong — null lah!
              </ExceptionLine>
              <ExceptionLine>
                <ExName>BoJioException</ExName>:<br />
                cannot tapau &apos;missing.boleh&apos; — not found!
              </ExceptionLine>
              <ExceptionLine>
                <ExName>ApaNiException</ExName>:<br />
                &apos;foo&apos; is not a function — tak boleh call
              </ExceptionLine>
              <ExceptionLine>
                <ExName>KantoiException</ExName>:<br />
                &apos;nama&apos; tu apa? Not found lah
              </ExceptionLine>
            </ExceptionStack>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon aria-hidden>🍛</FeatureIcon>
            <FeatureTitle>The full Nasi Campur</FeatureTitle>
            <FeatureText>
              Variables, loops, branches,{' '}
              <strong>jom</strong>, <strong>tapau</strong>,{' '}
              <strong>try see</strong>.
              <br />
              Like nasi campur: a bit of everything.
            </FeatureText>
            <FeatureSnippetStack>
              <FeatureSnippetBlock>{NASI_CAMPUR_SNIPPET}</FeatureSnippetBlock>
            </FeatureSnippetStack>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon aria-hidden>🏠</FeatureIcon>
            <FeatureTitle>Feels Like Home</FeatureTitle>
            <FeatureText>
              The keywords sound like everyday Malaysian English — not textbook jargon.
              <br />
              <strong>Abuden</strong>, <strong>jom</strong>, <strong>tapau</strong>,{' '}
              <strong>alamak</strong>… words you hear everywhere, from pasar to office.
              <br />
              That&apos;s why reading bolehlang feels like coming home.
            </FeatureText>
          </FeatureCard>
        </FeatureGrid>
      </Section>

      <SyntaxSection id="syntax">
        <SectionTitle>Keyword reference</SectionTitle>
        <SyntaxLead>
          A complete mapping of bolehlang keywords to their conventional equivalents, comfirm make you understand.
        </SyntaxLead>
        <KeywordTableWrap>
          <KeywordTable>
            <KwThead>
              <tr>
                <KwTh>bolehlang</KwTh>
                <KwTh>Conventional</KwTh>
                <KwTh>Meaning</KwTh>
              </tr>
            </KwThead>
            <tbody>
              {KEYWORD_ROWS.map((row) => (
                <tr key={row.boleh}>
                  <KwTdBoleh>{row.boleh}</KwTdBoleh>
                  <KwTdConv>{row.conventional}</KwTdConv>
                  <KwTdMean>{row.meaning}</KwTdMean>
                </tr>
              ))}
            </tbody>
          </KeywordTable>
        </KeywordTableWrap>
      </SyntaxSection>

      <SyntaxSection id="builtins">
        <SectionTitle>Built-in functions</SectionTitle>
        <SyntaxLead>
          Handy utilities baked right into the language, no need import one.
        </SyntaxLead>
        <KeywordTableWrap>
          <KeywordTable>
            <KwThead>
              <tr>
                <KwTh>Function</KwTh>
                <KwTh>Description</KwTh>
                <KwTh>Example</KwTh>
              </tr>
            </KwThead>
            <tbody>
              {BUILTIN_ROWS.map((row) => (
                <tr key={row.fn}>
                  <KwTdBoleh>{row.fn}</KwTdBoleh>
                  <KwTdConv>{row.desc}</KwTdConv>
                  <KwTdMean>{row.example}</KwTdMean>
                </tr>
              ))}
            </tbody>
          </KeywordTable>
        </KeywordTableWrap>
      </SyntaxSection>

      <CtaBand>
        <CtaInner>
          <CtaKicker>ENOUGH TALK.</CtaKicker>
          <CtaTitle>
            Time to chiong. Start building with Manglish today. Install the CLI and write your first .boleh program.
          </CtaTitle>
          <CtaActions>
            <CtaBtn href={GITHUB} target="_blank" rel="noreferrer">
              START CODING BOLEH
            </CtaBtn>
            <CtaBtnAlt
              href="https://buymeacoffee.com/kooixh"
              target="_blank"
              rel="noreferrer"
            >
              ☕ BELANJA KOPI
            </CtaBtnAlt>
          </CtaActions>
        </CtaInner>
      </CtaBand>

      <Footer>
        <FooterLogo>
          <LogoImg src="/bolehlang_logo.svg" alt="bolehlang logo" />
        </FooterLogo>
        <FooterNote>
          Made with <span aria-label="love">❤️</span> for Malaysians — by a Malaysian. {' '}
          <a href={GITHUB} target="_blank" rel="noreferrer">
            kooixh/bolehlang
          </a>
        </FooterNote>
        <FooterCopy>© {new Date().getFullYear()} BOLEHLANG · MIT · ALL RIGHTS RESERVED LAH</FooterCopy>
      </Footer>
    </Page>
  );
}
